// Explicit whole-shell groups only: never infer movable parts from meshes,
// reference designators, or arbitrary names inside a populated PCB.
export function bindShowcaseMotion(THREE, root, movingParts) {
  const parts = Object.entries(movingParts).map(([name, vector]) => {
    const matches = []
    root.traverse(object => { if (object.name === name) matches.push(object) })
    if (matches.length !== 1 || matches[0].isMesh || !Array.isArray(vector) ||
      vector.length !== 3 || !vector.every(Number.isFinite)) throw new Error("Invalid enclosure animation group")
    return { group: matches[0], base: matches[0].position.clone(), delta: new THREE.Vector3(...vector) }
  })
  if (!parts.length) throw new Error("No enclosure animation groups")
  return {
    apply(value) {
      const progress = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0
      parts.forEach(({ group, base, delta }) => group.position.copy(base).addScaledVector(delta, progress))
      root.updateMatrixWorld(true)
    },
  }
}

export function disposeShowcaseModel(root) {
  const geometries = new Set(), materials = new Set(), textures = new Set()
  root?.traverse(object => {
    if (object.geometry) geometries.add(object.geometry)
    const list = Array.isArray(object.material) ? object.material : [object.material]
    list.filter(Boolean).forEach(material => {
      materials.add(material)
      Object.values(material).forEach(value => { if (value?.isTexture) textures.add(value) })
    })
  })
  textures.forEach(texture => { texture.dispose(); texture.source?.data?.close?.() })
  materials.forEach(material => material.dispose())
  geometries.forEach(geometry => geometry.dispose())
}
// Perspective fit against all open/closed bounds, rather than a sphere that
// wastes most of a wide landing-page canvas around a long, narrow enclosure.
export function fitShowcaseDistance(THREE, corners, direction, fov, aspect) {
  const right = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), direction).normalize()
  const up = new THREE.Vector3().crossVectors(direction, right).normalize()
  const tanV = Math.tan(THREE.MathUtils.degToRad(fov / 2)), tanH = tanV * aspect
  return Math.max(...corners.map(p => p.dot(direction) + 1.12 * Math.max(Math.abs(p.dot(right)) / tanH, Math.abs(p.dot(up)) / tanV)))
}

// Orbit around the assembly, never through it. Only the camera moves; no board
// or component receives a transform when revealing the populated rear side.
export function showcaseOrbitPosition(THREE, from, to, progress) {
  const t = Math.max(0, Math.min(1, progress))
  const start = from.clone().normalize(), end = to.clone().normalize()
  const turn = new THREE.Quaternion().setFromUnitVectors(start, end)
  const rotation = new THREE.Quaternion().slerp(turn, t)
  return start.applyQuaternion(rotation).multiplyScalar(THREE.MathUtils.lerp(from.length(), to.length(), t))
}

